import { useMemo } from 'react'
import _ from 'lodash'
import { createTheme, ThemeOptions } from '@mui/material/styles'
import type { Theme as MuiTheme } from '@mui/material/styles'
import type { Theme } from '@emotion/react'

const useMuiTheme = ({
  theme,
  options,
}: {
  theme: Theme
  options?: ThemeOptions
}): MuiTheme => {
  return useMemo(() => {
    const baseTheme = {
      palette: {
        primary: {
          main: theme.primary,
        },
        secondary: {
          main: theme.secondary,
        },
      },
      shape: {
        borderRadius: 2,
      },
      typography: {
        fontFamily: '"Open Sans","Helvetica", "Arial", sans-serif',
        body1: {
          fontSize: '16px',
        },
        body2: {
          fontSize: '14px',
        },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              fontWeight: 600,
            },
            sizeSmall: {
              fontSize: '10px',
            },
            sizeMedium: {
              fontSize: '12px',
            },
            sizeLarge: {
              fontSize: '14px',
            },
            contained: {
              boxShadow: `
        0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)
      `,
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '24px',
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              padding: '9px 16px',
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            sizeSmall: {
              fontSize: '12px',
            },
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: {
              '--unstable_DataGrid-headWeight': 600,
              '--DataGrid-containerBackground': theme['secondary-50'],
              border: '1px solid #DFE0E1',
              '& .MuiDataGrid-pinnedRows': {
                backgroundColor: '#DFE0E1',
              },
            },
            columnHeaders: {
              backgroundColor: theme['secondary-50'],
              fontSize: 14,
              fontWeight: 500,
              boxShadow: '0px -1px 0px 0px #DEE0E3 inset',
              color: theme['secondary-700'],
              letterSpacing: 0.17,
              '.grouping-column-header': {
                fontSize: 0,
              },
              '.MuiDataGrid-columnHeader--sorted': {
                color: theme.primary,
                '& .MuiDataGrid-sortIcon': {
                  color: theme.primary,
                },
              },
            },
            row: {
              fontSize: 14,
              fontWeight: 400,
              letterSpacing: 0.17,
              color: theme['secondary-900'],
              backgroundColor: '#fff',
              '&.tree-child-row': {
                backgroundColor: theme['secondary-50'],
              },
              '&:hover': {
                backgroundColor: theme['secondary-light-500'],
              },
              '&.Mui-selected': {
                backgroundColor: theme['secondary-light-500'],
              },
            },
            cell: {
              '&.MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '&.MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
            },
          },
        },
      },
    }

    return createTheme(_.merge({}, baseTheme, options || {}))
  }, [theme, options])
}

export default useMuiTheme
